import { LogOut, Menu, Globe } from "lucide-react";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getProduct } from "@/data/products";
import { useLang } from "@/i18n";

const breadcrumbConfig = {
  home: [{ label: "Home", view: "home" }],
  product: [{ label: "Home", view: "home" }, { label: "Digital Menu", view: "product" }],
  countries: [{ label: "Home", view: "home" }, { label: "Digital Menu", view: "product" }, { label: "Country Examples", view: "countries" }],
  repository: [{ label: "Home", view: "home" }, { label: "Resources", view: "repository" }],
  data: [{ label: "Home", view: "home" }, { label: "For BU Owners", view: "data" }],
  onboarding: [{ label: "Home", view: "home" }, { label: "Onboarding", view: "onboarding" }],
  contacts: [{ label: "Home", view: "home" }, { label: "Contacts", view: "contacts" }],
};

const categoryAnchor = {
  "myFORA": "home#category-section-myfora",
  "Digital Menu": "home#category-section-digital-menu",
  "Ordering & Payments": "home#category-section-ordering-payments",
  "Engagement & Data": "home#category-section-engagement-data",
};

const productCategoryMap = {
  "myfora": "myFORA",
  "digital-menu": "Digital Menu",
  "pay-at-table": "Ordering & Payments",
  "pos-register": "Ordering & Payments",
  "pickup": "Ordering & Payments",
  "videri-signage": "Engagement & Data",
  "loyalty": "Engagement & Data",
  "rms": "Engagement & Data",
  "pepe-ai": "Engagement & Data",
};

const getCrumbs = (activeView) => {
  if (activeView.includes("#")) return breadcrumbConfig.home;
  if (activeView.startsWith("product:")) {
    const pid = activeView.split(":")[1];
    const p = getProduct(pid);
    const category = productCategoryMap[pid];
    const crumbs = [{ label: "Home", view: "home" }];
    if (category && categoryAnchor[category]) {
      crumbs.push({ label: category, view: categoryAnchor[category] });
    }
    crumbs.push({ label: p ? p.name : "Product", view: activeView });
    return crumbs;
  }
  return breadcrumbConfig[activeView] || breadcrumbConfig.home;
};

export const Header = ({ activeView, onNavigate, onToggleSidebar, onLogout }) => {
  const crumbs = getCrumbs(activeView);
  const { t, lang, setLang } = useLang();

  return (
    <header className="h-14 sm:h-16 bg-white border-b border-[#e8e8e8] flex items-center justify-between px-4 sm:px-8 shrink-0" data-testid="header">
      <div className="flex items-center gap-3">
        <button onClick={onToggleSidebar} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-sm hover:bg-[#fafafa] transition-colors" data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5 text-[#000000]" />
        </button>
        <Breadcrumb>
          <BreadcrumbList>
            {crumbs.map((crumb, idx) => {
              const isLast = idx === crumbs.length - 1;
              return (
                <span key={crumb.label} className="contents">
                  {idx > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="text-[#000000] font-semibold text-sm" data-testid={`breadcrumb-${crumb.view}`}>{t(crumb.label)}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink href="#" onClick={(e) => { e.preventDefault(); onNavigate(crumb.view); }} className="text-[#555555] hover:text-[#dd260e] text-sm" data-testid={`breadcrumb-${crumb.view}`}>{t(crumb.label)}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </span>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-3">
        {/* Language switcher */}
        <div
          className="flex items-center rounded-full border border-[#e8e8e8] overflow-hidden"
          data-testid="language-switcher"
        >
          <Globe className="w-3.5 h-3.5 text-[#555555] ml-2.5 mr-1 shrink-0" strokeWidth={2} />
          {["en", "it"].map((code) => (
            <button
              key={code}
              onClick={() => setLang(code)}
              className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
                lang === code ? "bg-[#000000] text-white" : "bg-white text-[#555555] hover:text-[#000000]"
              }`}
              data-testid={`language-option-${code}`}
              aria-label={code === "en" ? "English" : "Italiano"}
            >
              {code}
            </button>
          ))}
        </div>

        <AlertDialog>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertDialogTrigger asChild>
                  <button
                    className="w-9 h-9 rounded-full bg-[#e8e8e8] flex items-center justify-center hover:bg-[#dd260e] hover:text-white transition-colors text-[#555555]"
                    data-testid="logout-btn"
                    aria-label="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent className="bg-[#000000] text-white text-xs"><p>{t("Sign out")}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <AlertDialogContent data-testid="logout-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-condensed uppercase tracking-tight text-2xl">{t("Sign out of FORA Onboard?")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("You'll need to enter the access code again to come back in.")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="logout-cancel">{t("Stay signed in")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={onLogout}
                className="bg-[#dd260e] text-white hover:bg-[#b51e0c] font-bold uppercase tracking-[0.12em] text-xs"
                data-testid="logout-confirm"
              >
                {t("Sign out")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </header>
  );
};
